// src/components/LifecycleEventsList.js

import React, { useState, useEffect } from 'react';
import { Loader, Wrench, Recycle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const PRIMARY = '#326BDF';
const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
}

function StatusBadge({ status }) {
  const colors = {
    'In Progress':       { bg: '#FEF3C7', color: '#92400E' },
    'Completed':         { bg: '#D1FAE5', color: '#065F46' },
    'Cannot Repair':     { bg: '#FEE2E2', color: '#991B1B' },
    'Received':          { bg: '#EFF6FF', color: '#1E40AF' },
    'Disassembling':     { bg: '#FEF3C7', color: '#92400E' },
    'Components Sorted': { bg: '#F3E8FF', color: '#6B21A8' },
    'Complete':          { bg: '#D1FAE5', color: '#065F46' },
  };
  const style = colors[status] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      backgroundColor: style.bg, color: style.color,
    }}>
      {status}
    </span>
  );
}

function EventCard({ event }) {
  const [expanded, setExpanded] = useState(false);
  const isRepair  = event.event_type === 'Repair';
  const isRecycle = event.event_type === 'Recycle';
  const data      = event.event_data || {};

  return (
    <div style={{
      border: `1px solid ${isRepair ? '#BFDBFE' : '#BBF7D0'}`,
      borderRadius: 10, overflow: 'hidden',
      backgroundColor: '#fff',
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', cursor: 'pointer',
          backgroundColor: isRepair ? '#EFF6FF' : '#F0FDF4',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            backgroundColor: isRepair ? '#BFDBFE' : '#BBF7D0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {isRepair
              ? <Wrench size={15} color="#1D4ED8" />
              : <Recycle size={15} color="#15803D" />
            }
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>
              {isRepair ? 'Repair Event' : 'Recycling Event'}
            </p>
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
              {formatTs(event.created_at)} · {event.submitted_by}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isRepair && data.repair_status && <StatusBadge status={data.repair_status} />}
          {isRecycle && data.disassembly_stage && <StatusBadge status={data.disassembly_stage} />}
          {expanded
            ? <ChevronUp size={16} color="#6B7280" />
            : <ChevronDown size={16} color="#6B7280" />
          }
        </div>
      </div>

      {/* Details */}
      {expanded && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isRepair && (
            <>
              {data.component && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>COMPONENT</p>
                  <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{data.component}</p>
                </div>
              )}
              {data.notes && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>TECHNICIAN NOTES</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{data.notes}</p>
                </div>
              )}
            </>
          )}
          {isRecycle && (
            <>
              {data.disposal_method && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>DISPOSAL METHOD</p>
                  <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{data.disposal_method}</p>
                </div>
              )}
              {data.materials_recovered && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>MATERIALS RECOVERED</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{data.materials_recovered}</p>
                </div>
              )}
            </>
          )}
          {event.location_id && (
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>LOCATION</p>
              <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{event.location_id}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LifecycleEventsList({ dppId, userRole }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const isRepairOrRecycle = ['Repair/Refurb', 'Recycler', 'Manufacturer'].includes(userRole);

  useEffect(() => {
    if (!dppId || !isRepairOrRecycle) { setLoading(false); return; }
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/lifecycle-events/${dppId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (data.success) {
          setEvents(data.data || []);
        } else {
          setError(data.message || 'Failed to load events');
        }
      } catch (err) {
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [dppId]);

  if (!isRepairOrRecycle) return null;

  return (
    <div style={{
      border: '1px solid #E2EAF8', borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(50,107,223,0.06)',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid #E2EAF8',
        backgroundColor: '#F8FAFF',
      }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>
          {userRole === 'Manufacturer' ? 'All Lifecycle Events' : userRole === 'Repair/Refurb' ? 'Repair Events' : 'Recycling Events'}
        </h3>
        <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
          {loading ? 'Loading...' : `${events.length} event${events.length !== 1 ? 's' : ''} recorded`}
        </p>
      </div>

      {/* Content */}
      <div style={{ padding: 16, backgroundColor: '#fff' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9CA3AF', padding: '12px 0' }}>
            <Loader size={16} className="animate-spin" />
            <span style={{ fontSize: 13 }}>Loading events...</span>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B91C1C', padding: '8px 0' }}>
            <AlertTriangle size={14} />
            <span style={{ fontSize: 13 }}>{error}</span>
          </div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
            <p style={{ fontSize: 13, margin: 0 }}>
              No {userRole === 'Repair/Refurb' ? 'repair' : 'recycling'} events recorded yet.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {events.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LifecycleEventsList;